import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import { useAppSelector } from 'store';
import { selectCompany } from 'store/selectors/me/company';
import Card from 'storybookConfig/stories/cells/Card';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Body from 'storybookConfig/stories/molecules/Body';
import SocialLink from './SocialLink';
import { type MarketplaceFormData } from './types';

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  align-items: center;
  justify-content: center;

  @media (min-width: 1280px) {
    justify-content: space-between;
  }
`;

const GrayCard = styled(Card)`
  background-color: ${({ theme }) => theme.color.gray100};
`;

const BrandProfileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 400px;
  max-width: 100%;

  @media (min-width: 1280px) {
    flex: 2;
    max-width: calc(66.666% - 12px);
  }
`;

const BrandProfileCard = styled(Card)`
  width: 100%;
  overflow: hidden;
  border-radius: 16px;
  height: 300px;
  padding: 0;
`;

const BrandProfilePreview = styled.div<{ headerImage?: string }>`
  width: 100%;
  height: 230px;
  background-image: ${({ headerImage, theme }) =>
    headerImage ? `url(${headerImage})` : theme.color.carroMdGradient};
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: visible;
  background-color: ${({ headerImage }) => (headerImage ? 'transparent' : '#f0f0f0')};
`;

const ProfilePhotoContainer = styled.div`
  position: absolute;
  left: 50%;
  bottom: -33px;
  transform: translate(-50%, 0);
  padding: 4px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ProfilePhoto = styled.div<{ image?: string }>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-image: ${({ image }) => (image ? `url(${image})` : 'none')};
  background-size: cover;
  background-position: center;
  background-color: ${({ image }) => (image ? 'transparent' : '#f0f0f0')};
`;

const ContentSection = styled.div`
  padding: 30px 18px 18px;
  text-align: center;
  background: white;
`;

const CompanyName = styled(Heading)`
  color: #111827;
  font-size: 24px;
  margin-bottom: 6px;
  margin-top: 0;
`;

const BrandCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 250px;
  max-width: 100%;
  align-items: center;

  @media (min-width: 1280px) {
    flex: 1;
    max-width: calc(33.333% - 12px);
  }
`;

const BrandCardPreview = styled(Card)`
  width: 100%;
  max-width: 350px;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const BrandCardImage = styled.div<{ image?: string }>`
  width: 100%;
  aspect-ratio: 1;
  background-image: ${({ image }) => (image ? `url(${image})` : 'none')};
  background-size: cover;
  background-position: center;
  background-color: ${({ image }) => (image ? 'transparent' : '#f0f0f0')};
`;

const BrandCardContent = styled.div`
  padding: 24px;
  background: white;
`;

const SocialLinks = styled(Flex)`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.color.gray100};
`;

const TopBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  z-index: 1;
`;

const LikeButton = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    color: #666;
  }
`;

interface BrandAssetsProps {
  marketplaceData: MarketplaceFormData | undefined;
}

const BrandAssetsDescription = ({ marketplaceData }: BrandAssetsProps) => {
  const company = useAppSelector(selectCompany);

  return (
    <GrayCard>
      <Container>
        <BrandProfileWrapper>
          <BrandProfileCard>
            <BrandProfilePreview headerImage={marketplaceData?.brandHeaderPhoto}>
              <ProfilePhotoContainer>
                <ProfilePhoto image={marketplaceData?.brandProfilePhoto} />
              </ProfilePhotoContainer>
            </BrandProfilePreview>
            <ContentSection>
              <CompanyName variant="Headings/H2">{company?.name}</CompanyName>
            </ContentSection>
          </BrandProfileCard>
          <Body
            variant="Body/Regular"
            color="bodyTextPrimary"
            style={{
              fontSize: '16px',
              fontWeight: 600,
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginTop: '4px',
            }}
          >
            Brand Profile
          </Body>
        </BrandProfileWrapper>

        <BrandCardWrapper>
          <BrandCardPreview>
            <div style={{ position: 'relative' }}>
              <LikeButton>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </LikeButton>
              <TopBadge>Top 100</TopBadge>
              <BrandCardImage image={marketplaceData?.brandCardPhoto} />
            </div>
            <BrandCardContent>
              <Heading variant="Headings/H4">
                <div style={{ textAlign: 'center' }}>{company?.name}</div>
              </Heading>
              <SocialLinks gap="4px" wrap>
                {marketplaceData?.websiteURL && (
                  <SocialLink platform="website" url={marketplaceData?.websiteURL} iconOnly />
                )}
                {marketplaceData?.instagramURL && (
                  <SocialLink platform="instagram" url={marketplaceData?.instagramURL} iconOnly />
                )}
                {marketplaceData?.facebookURL && (
                  <SocialLink platform="facebook" url={marketplaceData?.facebookURL} iconOnly />
                )}
                {marketplaceData?.twitterURL && (
                  <SocialLink platform="twitter" url={marketplaceData?.twitterURL} iconOnly />
                )}
                {marketplaceData?.tikTokURL && (
                  <SocialLink platform="tikTok" url={marketplaceData?.tikTokURL} iconOnly />
                )}
                {marketplaceData?.linkedInURL && (
                  <SocialLink platform="linkedIn" url={marketplaceData?.linkedInURL} iconOnly />
                )}
              </SocialLinks>
            </BrandCardContent>
          </BrandCardPreview>
          <Body
            variant="Body/Regular"
            color="bodyTextPrimary"
            style={{
              fontSize: '16px',
              fontWeight: 600,
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginTop: '4px',
            }}
          >
            Brand Card
          </Body>
        </BrandCardWrapper>
      </Container>
    </GrayCard>
  );
};

export default BrandAssetsDescription;
